// "What Platonic noticed": one finding at a time in the insight colour the dashboard's key insight
// uses, with the way to read further and the way to ask about it. Both surfaces share wording
// from processNoticedText and evidence rules from processNoticed.
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  MessageCircleQuestion,
  Sparkles,
} from "lucide-react";
import React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { noticedText } from "./processNoticedText";
import type { Noticed } from "./processNoticed";

export type NoticedFinding = Noticed;

const ProcessNoticedCard = ({
  findings,
  nameById,
  onAsk,
  view,
}: {
  findings: NoticedFinding[];
  nameById: Map<string, string>;
  /** Opens the assistant on the finding, phrased from its headline and detail. */
  onAsk?: (text: { headline: string; detail: string }) => void;
  /** Where to read further about a finding, when there is somewhere to go. */
  view?: (finding: NoticedFinding) => { label: string; onClick: () => void } | null;
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const currentIndex = Math.min(index, findings.length - 1);
  const finding = findings[currentIndex];
  if (!finding) return null;
  const { headline, detail } = noticedText(finding, nameById, t);
  const action = view?.(finding) ?? null;
  const linkClass =
    "inline-flex items-center gap-1 rounded-md text-sm font-medium text-insight transition hover:text-insight-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-insight/30";
  const pagerClass =
    "inline-flex h-5 w-5 items-center justify-center rounded text-insight transition hover:bg-insight/10 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-insight/30";

  return (
    <section
      aria-label={t("processPanel.noticedTitle")}
      className="rounded-xl border border-insight-line bg-insight-soft px-4 py-3.5"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-semibold leading-snug tracking-tight text-insight">
          <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("processPanel.noticedTitle")}
        </h3>
        {findings.length > 1 && (
          <span className="flex items-center gap-0.5 text-xs tabular-nums text-insight">
            <button
              type="button"
              aria-label={t("processPanel.noticedPrevious")}
              disabled={currentIndex === 0}
              onClick={() => setIndex(currentIndex - 1)}
              className={pagerClass}
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            {t("processPanel.noticedPosition", {
              current: currentIndex + 1,
              total: findings.length,
            })}
            <button
              type="button"
              aria-label={t("processPanel.noticedNext")}
              disabled={currentIndex >= findings.length - 1}
              onClick={() => setIndex(currentIndex + 1)}
              className={pagerClass}
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        )}
      </div>
      <div aria-live="polite">
        <p className="text-base font-semibold leading-snug text-text">{headline}</p>
        <p className="mt-1 text-sm leading-relaxed text-text-mid">{detail}</p>
      </div>
      {(action || onAsk) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1">
          {action && (
            <button type="button" onClick={action.onClick} className={linkClass}>
              {action.label}
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
          {onAsk && (
            <button type="button" onClick={() => onAsk({ headline, detail })} className={linkClass}>
              <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
              {t("processPanel.noticedAsk")}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default ProcessNoticedCard;
